import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Flame, Sprout, Sparkles, Inbox, Send, AlertTriangle, Clock, Brain, Hand } from "lucide-react";

type Row = {
  org_id: string;
  fired_24h: number;
  seeded_24h: number;
  jobs_24h: number;
  ready_24h: number;
  awaiting_admin_24h: number;
  delivered_24h: number;
  picked_up_24h: number;
  failed_24h: number;
  timeouts_24h: number;
  used_in_reply_24h: number;
  funnel_status:
    | "no_triggers"
    | "all_seeds_skipped"
    | "no_insights_ready"
    | "ready_not_delivered"
    | "delivered_not_picked_up"
    | "picked_up_not_used"
    | "delivered_not_used"
    | "healthy";
};

type Totals = Omit<Row, "org_id" | "funnel_status">;

const STATUS_LABEL: Record<Row["funnel_status"], { label: string; tone: string }> = {
  healthy: { label: "Funil saudável", tone: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" },
  picked_up_not_used: { label: "Capturado mas não usado em resposta", tone: "bg-amber-500/15 text-amber-700 dark:text-amber-300" },
  delivered_not_picked_up: { label: "Entregue mas pipeline não capturou", tone: "bg-amber-500/15 text-amber-700 dark:text-amber-300" },
  delivered_not_used: { label: "Entregue mas não usado em resposta", tone: "bg-amber-500/15 text-amber-700 dark:text-amber-300" },
  ready_not_delivered: { label: "Insights presos sem entrega", tone: "bg-amber-500/15 text-amber-700 dark:text-amber-300" },
  no_insights_ready: { label: "Jobs criados, nenhum amadureceu", tone: "bg-amber-500/15 text-amber-700 dark:text-amber-300" },
  all_seeds_skipped: { label: "Triggers disparam, mas seed bloqueia 100%", tone: "bg-red-500/15 text-red-700 dark:text-red-300" },
  no_triggers: { label: "Nenhum trigger nas últimas 24h", tone: "bg-muted text-muted-foreground" },
};

const CARDS = [
  { key: "fired_24h" as const, label: "Gate disparou", icon: Flame, hint: "dmn_fired" },
  { key: "seeded_24h" as const, label: "Seed criado", icon: Sprout, hint: "dmn_seeded" },
  { key: "ready_24h" as const, label: "Insight pronto", icon: Sparkles, hint: "ready+inbox" },
  { key: "awaiting_admin_24h" as const, label: "No inbox", icon: Inbox, hint: "awaiting_admin" },
  { key: "delivered_24h" as const, label: "Entregue", icon: Send, hint: "delivered_at" },
  { key: "picked_up_24h" as const, label: "Capturado pelo pipeline", icon: Hand, hint: "picked_up_at" },
  { key: "used_in_reply_24h" as const, label: "Usado na resposta", icon: Brain, hint: "dmn_used_in_reply" },
  { key: "timeouts_24h" as const, label: "Timeouts", icon: Clock, hint: "dmn_timeout" },
  { key: "failed_24h" as const, label: "Jobs falhos", icon: AlertTriangle, hint: "state=failed" },
];

export default function CortexDmnFunnelCards() {
  const [totals, setTotals] = useState<Totals | null>(null);
  const [status, setStatus] = useState<Row["funnel_status"]>("no_triggers");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data, error } = await supabase
        .from("cortex_dmn_funnel_view" as any)
        .select("*");
      if (!mounted) return;
      const empty: Totals = {
        fired_24h: 0,
        seeded_24h: 0,
        jobs_24h: 0,
        ready_24h: 0,
        awaiting_admin_24h: 0,
        delivered_24h: 0,
        picked_up_24h: 0,
        failed_24h: 0,
        timeouts_24h: 0,
        used_in_reply_24h: 0,
      };
      if (error || !data) {
        setTotals(empty);
        setLoading(false);
        return;
      }
      const sum: Totals = { ...empty };
      let worst: Row["funnel_status"] = "healthy";
      const worstOrder: Row["funnel_status"][] = [
        "healthy",
        "no_triggers",
        "picked_up_not_used",
        "delivered_not_picked_up",
        "delivered_not_used",
        "ready_not_delivered",
        "no_insights_ready",
        "all_seeds_skipped",
      ];
      for (const r of data as unknown as Row[]) {
        sum.fired_24h += Number(r.fired_24h ?? 0);
        sum.seeded_24h += Number(r.seeded_24h ?? 0);
        sum.jobs_24h += Number(r.jobs_24h ?? 0);
        sum.ready_24h += Number(r.ready_24h ?? 0);
        sum.awaiting_admin_24h += Number(r.awaiting_admin_24h ?? 0);
        sum.delivered_24h += Number(r.delivered_24h ?? 0);
        sum.picked_up_24h += Number(r.picked_up_24h ?? 0);
        sum.failed_24h += Number(r.failed_24h ?? 0);
        sum.timeouts_24h += Number(r.timeouts_24h ?? 0);
        sum.used_in_reply_24h += Number(r.used_in_reply_24h ?? 0);
        if (worstOrder.indexOf(r.funnel_status) > worstOrder.indexOf(worst)) {
          worst = r.funnel_status;
        }
      }
      setTotals(sum);
      setStatus(worst);
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const conv = (a: number, b: number) =>
    b === 0 ? "—" : `${Math.round((a / b) * 100)}%`;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium">Funil DMN — últimas 24h</h3>
          <p className="text-xs text-muted-foreground">
            fired → seeded → ready → delivered → capturado → usado na resposta
          </p>
        </div>
        {!loading && totals && (
          <Badge variant="outline" className={STATUS_LABEL[status].tone}>
            {STATUS_LABEL[status].label}
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-9 gap-3">
        {CARDS.map(({ key, label, icon: Icon, hint }) => (
          <Card key={key}>
            <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-xs font-medium text-muted-foreground">
                {label}
              </CardTitle>
              <Icon className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {loading || !totals ? (
                <Skeleton className="h-7 w-12" />
              ) : (
                <div className="text-2xl font-bold tabular-nums">
                  {totals[key]}
                </div>
              )}
              <p className="text-[10px] text-muted-foreground mt-1 font-mono truncate">
                {hint}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {!loading && totals && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs text-muted-foreground">
          <div className="rounded border border-border/50 px-3 py-2">
            <div className="font-medium text-foreground">
              {conv(totals.seeded_24h, totals.fired_24h)}
            </div>
            <div>fired → seeded</div>
          </div>
          <div className="rounded border border-border/50 px-3 py-2">
            <div className="font-medium text-foreground">
              {conv(totals.ready_24h, totals.seeded_24h)}
            </div>
            <div>seeded → ready</div>
          </div>
          <div className="rounded border border-border/50 px-3 py-2">
            <div className="font-medium text-foreground">
              {conv(totals.delivered_24h, totals.ready_24h)}
            </div>
            <div>ready → delivered</div>
          </div>
          <div className="rounded border border-border/50 px-3 py-2">
            <div className="font-medium text-foreground">
              {conv(totals.picked_up_24h, totals.delivered_24h)}
            </div>
            <div>delivered → capturado</div>
          </div>
          <div className="rounded border border-border/50 px-3 py-2">
            <div className="font-medium text-foreground">
              {conv(totals.used_in_reply_24h, totals.picked_up_24h)}
            </div>
            <div>capturado → usado</div>
          </div>
        </div>
      )}
    </div>
  );
}
